import React, { useState, useEffect } from 'react';
import {
    X, ChevronRight, ChevronLeft, Check, Wand2,
    ShieldAlert, Info, Plus, Loader2, Clock, Coffee,
    Briefcase, Calendar, Sliders, Sparkles, CheckCircle2,
    Moon, Trash2, CalendarRange, Layers
} from 'lucide-react';
import { apiClient } from '../../utils/apiClient';

const STEPS = [
    { id: 1, title: 'İsim & Günler', subtitle: 'Şablon ve Takvim', icon: Calendar },
    { id: 2, title: 'Model & Saatler', subtitle: 'Esneklik ve Süre', icon: Clock },
    { id: 3, title: 'Tolerans & Sapma', subtitle: 'Giriş/Çıkış Sınırları', icon: Sliders },
    { id: 4, title: 'Mola Düzeni', subtitle: 'Düşüm Politikası', icon: Coffee },
    { id: 5, title: 'Fazla Mesai', subtitle: 'Eşik ve Tavan', icon: Sparkles },
    { id: 6, title: 'Mimari Özet', subtitle: 'Onay ve Kayıt', icon: Layers }
];

const SHIFT_TYPE_CARDS = [
    {
        id: 'Standard',
        title: 'Sabit Vardiya',
        badge: 'Standart',
        desc: 'Sabit giriş ve çıkış saatleri (Örn: 09:00 - 18:00)'
    },
    {
        id: 'FlexibleWindow',
        title: 'Pencereli Esnek',
        badge: 'Sliding Window',
        desc: 'Belirli saat aralığında (Örn: 08:00 - 10:00) esnek giriş'
    },
    {
        id: 'FlexibleDaily',
        title: 'Günlük Esnek',
        badge: 'Daily Flex',
        desc: 'Saat bağımsız, üstteki saatlerden otomatik günlük net hedef'
    },
    {
        id: 'FlexibleWeekly',
        title: 'Haftalık Esnek',
        badge: 'Weekly Flex',
        desc: 'Haftalık kümülatif hedef süre takibi (Örn: 45 saat / 2700 dk)'
    }
];

const SHIFT_TYPE_LABELS = {
    Standard: 'Sabit Vardiya (Standart)',
    FlexibleWindow: 'Pencereli Esnek Mesai',
    FlexibleDaily: 'Günlük Esnek Mesai',
    FlexibleWeekly: 'Haftalık Esnek Mesai'
};

const DAYS_LIST = [
    { key: 'Monday', short: 'Pzt', full: 'Pazartesi' },
    { key: 'Tuesday', short: 'Sal', full: 'Salı' },
    { key: 'Wednesday', short: 'Çar', full: 'Çarşamba' },
    { key: 'Thursday', short: 'Per', full: 'Perşembe' },
    { key: 'Friday', short: 'Cum', full: 'Cuma' },
    { key: 'Saturday', short: 'Cmt', full: 'Cumartesi' },
    { key: 'Sunday', short: 'Paz', full: 'Pazar' }
];

const getInitialWizardData = () => ({
    // Adım 1: Grup ve Günler
    groupName: '',
    shortCode: '',
    workDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],

    // Adım 2: Model ve Saatler
    workModel: 'fixed', // 'fixed', 'variable'
    shiftType: 'Standard', // 'Standard', 'FlexibleWindow', 'FlexibleDaily', 'FlexibleWeekly'
    calculationMethod: 'BasedOnFirstEntryLastExit',
    shifts: [{ id: Date.now(), startTime: '09:00', endTime: '18:00', isNextDayShift: false, assignToShiftStartDate: true }],
    flexibleEntryStartTime: '08:00',
    flexibleEntryEndTime: '10:00',
    limitEarlyEntryToWindow: true,
    weeklyFlexStartDay: 'Monday',
    convertRemainingDaysToHoliday: true,
    giveOvertimeOnRemainingDays: true,
    requiredDurationMinutes: '', // Sadece FlexibleWeekly için haftalık hedef
    shiftCatchmentStartHours: 2,
    shiftCatchmentEndHours: 2,

    // Adım 3: Toleranslar ve Kesintiler
    entryToleranceMinutes: 5,
    exitToleranceMinutes: 5,
    deductLateEntryFromActual: false,
    deductEarlyExitFromActual: false,
    minValidWorkMinutes: 60,

    // Adım 4: Molalar
    hasBreak: true,
    breakName: 'Öğle Molası',
    breakType: 'FixedTime', // 'FixedTime', 'FlexibleWindow', 'FlexibleAnytime'
    breakWindowStartTime: '12:00',
    breakWindowEndTime: '13:00',
    breakDurationMinutes: 60,
    breakIsPaid: false,
    breakDeductionPolicy: 'DeductAlways', // 'DeductAlways', 'DeductActualUsage'
    breakUnusedPolicy: 'ForceDeduct', // 'ForceDeduct', 'PayToEmployee'
    breakConvertUnusedToOvertime: false,
    breakIgnoreOnHolidays: false,

    // Adım 5: Fazla Mesai (FM) Kuralları
    maxOvertimeMinutes: 120,
    earlyOvertimeEnabled: false,
    earlyOvertimeThresholdMinutes: 30,
    lateOvertimeEnabled: true,
    lateOvertimeThresholdMinutes: 30
});

export default function ShiftWizardModal({ isOpen, onClose, onSave }) {
    const [currentStep, setCurrentStep] = useState(1);
    const [validationError, setValidationError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [wizardData, setWizardData] = useState(getInitialWizardData);
    const [weeklyDurationUnit, setWeeklyDurationUnit] = useState('minutes');

    useEffect(() => {
        if (isOpen) {
            setCurrentStep(1);
            setValidationError('');
            setWizardData(getInitialWizardData());
            setWeeklyDurationUnit('minutes');
        }
    }, [isOpen]);

    // Sabit zamanlı molada pencere saatlerinden otomatik süre hesabı
    useEffect(() => {
        if (wizardData.hasBreak && wizardData.breakType === 'FixedTime' && wizardData.breakWindowStartTime && wizardData.breakWindowEndTime) {
            const s = wizardData.breakWindowStartTime.split(':');
            const e = wizardData.breakWindowEndTime.split(':');
            if (s.length === 2 && e.length === 2) {
                let sMins = parseInt(s[0], 10) * 60 + parseInt(s[1], 10);
                let eMins = parseInt(e[0], 10) * 60 + parseInt(e[1], 10);
                if (eMins < sMins) eMins += 24 * 60;
                const diff = eMins - sMins;
                if (diff > 0 && wizardData.breakDurationMinutes !== diff) {
                    setWizardData(prev => ({ ...prev, breakDurationMinutes: diff }));
                }
            }
        }
    }, [wizardData.hasBreak, wizardData.breakType, wizardData.breakWindowStartTime, wizardData.breakWindowEndTime, wizardData.breakDurationMinutes]);

    if (!isOpen) return null;

    const isFlexDurationModel = wizardData.shiftType === 'FlexibleDaily' || wizardData.shiftType === 'FlexibleWeekly';

    // İlk vardiyanın brüt ve otomatik net çalışma süresini hesapla
    const calcShiftDurationInfo = (shiftObj) => {
        const s = shiftObj || wizardData.shifts?.[0];
        if (!s || !s.startTime || !s.endTime) {
            return { gross: 540, unpaidBreak: 60, net: 480 };
        }
        const p1 = s.startTime.split(':');
        const p2 = s.endTime.split(':');
        let st = (parseInt(p1[0], 10) || 0) * 60 + (parseInt(p1[1], 10) || 0);
        let et = (parseInt(p2[0], 10) || 0) * 60 + (parseInt(p2[1], 10) || 0);
        if (et <= st) et += 24 * 60;
        const gross = Math.max(0, et - st);
        const unpaidBreak = (wizardData.hasBreak && !wizardData.breakIsPaid)
            ? (parseInt(wizardData.breakDurationMinutes, 10) || 0)
            : 0;
        const net = Math.max(0, gross - unpaidBreak);
        return { gross, unpaidBreak, net };
    };

    const firstShiftCalc = calcShiftDurationInfo(wizardData.shifts?.[0]);
    const autoDailyNetMinutes = firstShiftCalc.net;

    const applyPresetTemplate = (presetKey) => {
        setValidationError('');
        const base = getInitialWizardData();
        if (presetKey === 'office_standard') {
            setWizardData({
                ...base,
                groupName: 'Merkez Ofis Mesaisi',
                shortCode: 'OFS',
                workDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                shiftType: 'Standard',
                shifts: [{ id: Date.now(), startTime: '09:00', endTime: '18:00', isNextDayShift: false, assignToShiftStartDate: true }],
                hasBreak: true,
                breakName: 'Öğle Molası',
                breakType: 'FixedTime',
                breakWindowStartTime: '12:00',
                breakWindowEndTime: '13:00',
                breakDurationMinutes: 60,
                breakIsPaid: false
            });
        } else if (presetKey === 'flex_window') {
            setWizardData({
                ...base,
                groupName: 'Pencereli Esnek Ofis',
                shortCode: 'ESN',
                workDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                shiftType: 'FlexibleWindow',
                flexibleEntryStartTime: '07:30',
                flexibleEntryEndTime: '09:30',
                limitEarlyEntryToWindow: true,
                shifts: [{ id: Date.now(), startTime: '09:00', endTime: '18:00', isNextDayShift: false, assignToShiftStartDate: true }]
            });
        } else if (presetKey === 'flex_daily') {
            setWizardData({
                ...base,
                groupName: 'Günlük Esnek Ar-Ge Mesaisi',
                shortCode: 'GUN',
                workDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
                shiftType: 'FlexibleDaily',
                shiftCatchmentStartHours: 0,
                earlyOvertimeEnabled: false,
                shifts: [{ id: Date.now(), startTime: '09:00', endTime: '18:00', isNextDayShift: false, assignToShiftStartDate: true }]
            });
        } else if (presetKey === 'flex_weekly') {
            setWizardData({
                ...base,
                groupName: 'Haftalık 45 Saat Esnek Mesai',
                shortCode: 'HFT',
                workDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                shiftType: 'FlexibleWeekly',
                requiredDurationMinutes: 2700,
                shiftCatchmentStartHours: 0,
                earlyOvertimeEnabled: false,
                convertRemainingDaysToHoliday: true,
                giveOvertimeOnRemainingDays: true,
                shifts: [{ id: Date.now(), startTime: '09:00', endTime: '18:00', isNextDayShift: false, assignToShiftStartDate: true }]
            });
        } else if (presetKey === 'factory_3shift') {
            setWizardData({
                ...base,
                groupName: 'Üretim 3 Vardiya Grubu',
                shortCode: 'URT',
                workDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                workModel: 'variable',
                shiftType: 'Standard',
                shifts: [
                    { id: Date.now() + 1, startTime: '08:00', endTime: '16:00', isNextDayShift: false, assignToShiftStartDate: true },
                    { id: Date.now() + 2, startTime: '16:00', endTime: '00:00', isNextDayShift: false, assignToShiftStartDate: true },
                    { id: Date.now() + 3, startTime: '00:00', endTime: '08:00', isNextDayShift: true, assignToShiftStartDate: true }
                ],
                hasBreak: true,
                breakName: 'Vardiya Yemek Molası',
                breakType: 'FlexibleAnytime',
                breakDurationMinutes: 30,
                breakIsPaid: false
            });
        }
    };

    const handleNext = () => {
        setValidationError('');

        if (currentStep === 1) {
            if (!wizardData.groupName || !wizardData.groupName.trim()) {
                setValidationError('Lütfen ilerlemeden önce "Vardiya / Grup Adı" bilgisini doldurunuz.');
                return;
            }
            if (wizardData.workDays.length === 0) {
                setValidationError('Lütfen en az bir çalışma günü seçiniz.');
                return;
            }
        }

        if (currentStep === 2) {
            for (let i = 0; i < wizardData.shifts.length; i++) {
                if (!wizardData.shifts[i].startTime || !wizardData.shifts[i].endTime) {
                    setValidationError('Lütfen tüm vardiyalar için Giriş ve Çıkış saatlerini eksiksiz giriniz.');
                    return;
                }
            }
            if (wizardData.shiftType === 'FlexibleWindow' && (!wizardData.flexibleEntryStartTime || !wizardData.flexibleEntryEndTime)) {
                setValidationError('Pencereli Esnek modelde lütfen Esnek Giriş Başlangıç ve Bitiş saatlerini giriniz.');
                return;
            }
            if (wizardData.shiftType === 'FlexibleWeekly' && (!wizardData.requiredDurationMinutes || parseInt(wizardData.requiredDurationMinutes, 10) <= 0)) {
                setValidationError('Haftalık Esnek mesai modelinde lütfen haftalık zorunlu hedef çalışma süresini giriniz.');
                return;
            }
        }

        if (currentStep === 4 && wizardData.hasBreak) {
            if (!wizardData.breakName || !wizardData.breakName.trim()) {
                setValidationError('Lütfen mola adını giriniz.');
                return;
            }
            if ((wizardData.breakType === 'FixedTime' || wizardData.breakType === 'FlexibleWindow') && (!wizardData.breakWindowStartTime || !wizardData.breakWindowEndTime)) {
                setValidationError('Lütfen mola pencere başlangıç ve bitiş saatlerini giriniz.');
                return;
            }
            if (!wizardData.breakDurationMinutes || wizardData.breakDurationMinutes <= 0) {
                setValidationError('Lütfen geçerli bir mola süresi (dakika) giriniz.');
                return;
            }
        }

        setCurrentStep(prev => Math.min(prev + 1, 6));
    };

    const handlePrev = () => {
        setValidationError('');
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    const updateData = (field, value) => {
        setValidationError('');
        setWizardData(prev => {
            const next = { ...prev, [field]: value };
            if (field === 'shiftType') {
                if (value === 'FlexibleWeekly') {
                    next.workModel = 'fixed';
                    next.shifts = [prev.shifts[0]];
                    next.shiftCatchmentStartHours = 0;
                    next.earlyOvertimeEnabled = false;
                    if (!prev.requiredDurationMinutes || prev.requiredDurationMinutes <= 600) {
                        next.requiredDurationMinutes = 2700;
                    }
                } else if (value === 'FlexibleDaily') {
                    next.workModel = 'fixed';
                    next.shifts = [prev.shifts[0]];
                    next.shiftCatchmentStartHours = 0;
                    next.earlyOvertimeEnabled = false;
                    next.requiredDurationMinutes = '';
                } else {
                    next.requiredDurationMinutes = '';
                    if (value === 'FlexibleWindow' && prev.limitEarlyEntryToWindow) {
                        next.earlyOvertimeEnabled = false;
                    }
                }
            }
            if (field === 'limitEarlyEntryToWindow' && value === true && prev.shiftType === 'FlexibleWindow') {
                next.earlyOvertimeEnabled = false;
            }
            if (field === 'workModel' && value === 'fixed' && prev.shifts.length > 1) {
                next.shifts = [prev.shifts[0]];
            }
            return next;
        });
    };

    const handleAddShift = () => {
        setWizardData(prev => ({
            ...prev,
            shifts: [...prev.shifts, { id: Date.now(), startTime: '16:00', endTime: '00:00', isNextDayShift: false, assignToShiftStartDate: true }]
        }));
    };

    const handleRemoveShift = (idToRemove) => {
        setWizardData(prev => ({
            ...prev,
            shifts: prev.shifts.filter(s => s.id !== idToRemove)
        }));
    };

    const updateShift = (id, field, value) => {
        setWizardData(prev => ({
            ...prev,
            shifts: prev.shifts.map(s => {
                if (s.id !== id) return s;
                const updated = { ...s, [field]: value };
                if ((field === 'startTime' || field === 'endTime') && updated.startTime && updated.endTime) {
                    updated.isNextDayShift = updated.endTime <= updated.startTime;
                }
                return updated;
            })
        }));
    };

    const formatTimeForSpan = (val) => {
        if (!val) return null;
        return val.length === 5 ? `${val}:00` : val;
    };

    const handleFinish = async () => {
        setValidationError('');
        setIsSaving(true);
        try {
            const needsBreakWindow = wizardData.hasBreak && (wizardData.breakType === 'FixedTime' || wizardData.breakType === 'FlexibleWindow');
            const resolvedRequiredMinutes = wizardData.shiftType === 'FlexibleWeekly'
                ? (parseInt(wizardData.requiredDurationMinutes, 10) || 2700)
                : (wizardData.shiftType === 'FlexibleDaily' ? autoDailyNetMinutes : null);

            const payload = {
                ...wizardData,
                flexibleEntryStartTime: wizardData.shiftType === 'FlexibleWindow' ? wizardData.flexibleEntryStartTime : null,
                flexibleEntryEndTime: wizardData.shiftType === 'FlexibleWindow' ? wizardData.flexibleEntryEndTime : null,
                weeklyFlexStartDay: wizardData.shiftType === 'FlexibleWeekly' ? wizardData.weeklyFlexStartDay : 'Monday',
                requiredDurationMinutes: resolvedRequiredMinutes,
                shiftCatchmentStartHours: isFlexDurationModel ? 0 : wizardData.shiftCatchmentStartHours,
                earlyOvertimeEnabled: ((wizardData.shiftType === 'FlexibleWindow' && wizardData.limitEarlyEntryToWindow) || isFlexDurationModel)
                    ? false
                    : wizardData.earlyOvertimeEnabled,
                breakWindowStartTime: needsBreakWindow ? formatTimeForSpan(wizardData.breakWindowStartTime) : null,
                breakWindowEndTime: needsBreakWindow ? formatTimeForSpan(wizardData.breakWindowEndTime) : null,
            };

            const response = await apiClient('Scheduling/wizard', {
                method: 'POST',
                body: payload
            });

            if (response && response.success) {
                if (onSave) onSave(response);
            } else {
                setValidationError(response?.message || 'Kayıt sırasında bir hata oluştu.');
            }
        } catch (error) {
            setValidationError(error.message || 'Kayıt sırasında sunucu hatası oluştu.');
        } finally {
            setIsSaving(false);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="space-y-6">
                        {/* Hızlı Şablon Seçici */}
                        <div className="p-4 rounded-2xl bg-gradient-to-r from-indigo-50/80 via-purple-50/60 to-blue-50/80 dark:from-indigo-950/40 dark:via-purple-950/30 dark:to-blue-950/40 border border-indigo-200/80 dark:border-indigo-800/60">
                            <div className="flex items-center justify-between mb-2.5">
                                <span className="text-xs font-bold uppercase tracking-wider text-indigo-800 dark:text-indigo-300 flex items-center gap-1.5">
                                    <Sparkles size={14} className="text-indigo-600" />
                                    Hızlı Başlangıç Şablonları (İsteğe Bağlı Tek Tıkla Doldur)
                                </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                                {[
                                    { key: 'office_standard', title: 'Standart Ofis', sub: '09:00-18:00 • Pzt-Cum' },
                                    { key: 'flex_window', title: 'Pencereli Esnek', sub: '07:30-09:30 Giriş' },
                                    { key: 'flex_daily', title: 'Günlük Esnek', sub: 'Saat Bağımsız 8 Sa' },
                                    { key: 'flex_weekly', title: 'Haftalık Esnek', sub: '45 Saat Kümülatif' },
                                    { key: 'factory_3shift', title: 'Üretim 3 Vardiya', sub: '08-16 / 16-24 / 00-08' }
                                ].map(item => (
                                    <button
                                        key={item.key}
                                        type="button"
                                        onClick={() => applyPresetTemplate(item.key)}
                                        className="p-2.5 rounded-xl bg-white dark:bg-gray-800 border border-indigo-200/70 dark:border-gray-700 hover:border-indigo-500 hover:shadow-sm text-left transition-all"
                                    >
                                        <div className="text-xs font-bold text-gray-900 dark:text-white">{item.title}</div>
                                        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{item.sub}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* İsim ve Kısa Kod */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                    Vardiya / Çalışma Düzeni Adı
                                </label>
                                <input
                                    type="text"
                                    placeholder="Örn: Merkez Ofis Beyaz Yaka Mesaisi"
                                    value={wizardData.groupName}
                                    onChange={(e) => updateData('groupName', e.target.value)}
                                    className="w-full px-3.5 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Bu isimle Vardiya, Vardiya Grubu ve Çalışma Periyodu otomatik oluşturulacaktır.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                    Kısa Kod (Puantaj Cetveli)
                                </label>
                                <input
                                    type="text"
                                    placeholder="Örn: OFS, URT, ESN"
                                    value={wizardData.shortCode}
                                    onChange={(e) => updateData('shortCode', e.target.value.toUpperCase())}
                                    className="w-full px-3.5 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono uppercase focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                        </div>

                        {/* Haftalık Çalışma Günleri Seçimi */}
                        <div className="p-5 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div>
                                    <label className="block text-sm font-bold text-gray-900 dark:text-white">
                                        Haftalık Çalışma Günleri ({wizardData.workDays.length} İş Günü / {7 - wizardData.workDays.length} Hafta Tatili)
                                    </label>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Seçilmeyen günler Çalışma Periyodunda otomatik olarak <strong>"Hafta Tatili (HT)"</strong> olarak işaretlenir.
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    <button
                                        type="button"
                                        onClick={() => updateData('workDays', ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'])}
                                        className="px-2.5 py-1 text-xs font-bold rounded-lg bg-indigo-100 text-indigo-700 hover:bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300"
                                    >
                                        Pzt-Cum (5 Gün)
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => updateData('workDays', ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'])}
                                        className="px-2.5 py-1 text-xs font-bold rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300"
                                    >
                                        Pzt-Cmt (6 Gün)
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => updateData('workDays', DAYS_LIST.map(d => d.key))}
                                        className="px-2.5 py-1 text-xs font-bold rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300"
                                    >
                                        7 Gün
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 sm:grid-cols-7 gap-2.5">
                                {DAYS_LIST.map((day) => {
                                    const isSelected = wizardData.workDays.includes(day.key);
                                    return (
                                        <button
                                            type="button"
                                            key={day.key}
                                            onClick={() => {
                                                const newDays = isSelected
                                                    ? wizardData.workDays.filter(d => d !== day.key)
                                                    : [...wizardData.workDays, day.key];
                                                updateData('workDays', newDays);
                                            }}
                                            className={`p-3 rounded-xl border text-center transition-all flex flex-col items-center justify-center ${
                                                isSelected
                                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-500/20'
                                                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            <span className="text-xs font-extrabold">{day.short}</span>
                                            <span className={`text-[10px] mt-1 font-semibold px-1.5 py-0.5 rounded-full ${
                                                isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                                            }`}>
                                                {isSelected ? 'Çalışma' : 'Tatil (HT)'}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="space-y-6">
                        {/* 4 Görsel Vardiya Türü Kartı */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                                1. Vardiya Çalışma ve Esneklik Modeli
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {SHIFT_TYPE_CARDS.map(card => {
                                    const selected = wizardData.shiftType === card.id;
                                    return (
                                        <button
                                            key={card.id}
                                            type="button"
                                            onClick={() => updateData('shiftType', card.id)}
                                            className={`p-3.5 rounded-xl border text-left transition-all flex flex-col justify-between ${
                                                selected
                                                    ? 'border-indigo-600 bg-indigo-50/70 dark:bg-indigo-900/30 ring-2 ring-indigo-500/20'
                                                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40'
                                            }`}
                                        >
                                            <div>
                                                <span className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-full mb-1.5 ${
                                                    selected ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'
                                                }`}>
                                                    {card.badge}
                                                </span>
                                                <div className="font-bold text-sm text-gray-900 dark:text-white">{card.title}</div>
                                            </div>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5 leading-snug">{card.desc}</p>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Hesaplama Yöntemi Kartları */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                                2. Puantaj Süre Hesaplama Yöntemi
                            </label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => updateData('calculationMethod', 'BasedOnFirstEntryLastExit')}
                                    className={`p-3.5 rounded-xl border text-left transition-all ${
                                        wizardData.calculationMethod === 'BasedOnFirstEntryLastExit'
                                            ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20 ring-2 ring-indigo-500/20'
                                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="font-bold text-sm text-gray-900 dark:text-white">İlk Giriş – Son Çıkış Arası</div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        Günün ilk girişi ile son çıkışı arasındaki brüt süreden mola kuralları düşülür.
                                    </p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => updateData('calculationMethod', 'BasedOnTimeSpentInside')}
                                    className={`p-3.5 rounded-xl border text-left transition-all ${
                                        wizardData.calculationMethod === 'BasedOnTimeSpentInside'
                                            ? 'border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20 ring-2 ring-indigo-500/20'
                                            : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="font-bold text-sm text-gray-900 dark:text-white">İçeride Kalınan Toplam Süre (Turnike Çiftleri)</div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        Gün içindeki tüm Giriş-Çıkış çiftleri toplanır; dışarıda geçen boşluklar molalarla eşleştirilir.
                                    </p>
                                </button>
                            </div>
                        </div>

                        {/* Tekil / Çoklu Vardiya Seçimi (Sabit ve Pencereli Esnek için) */}
                        {!isFlexDurationModel && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => updateData('workModel', 'fixed')}
                                    className={`p-3 rounded-xl border text-left transition-all ${
                                        wizardData.workModel === 'fixed'
                                            ? 'border-blue-600 bg-blue-50/60 dark:bg-blue-900/20 ring-2 ring-blue-500/20'
                                            : 'border-gray-200 dark:border-gray-700'
                                    }`}
                                >
                                    <div className="font-bold text-sm text-gray-900 dark:text-white">Tekil Vardiya Planı</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Personel tek bir vardiya saat diliminde çalışır.</div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => updateData('workModel', 'variable')}
                                    className={`p-3 rounded-xl border text-left transition-all ${
                                        wizardData.workModel === 'variable'
                                            ? 'border-blue-600 bg-blue-50/60 dark:bg-blue-900/20 ring-2 ring-blue-500/20'
                                            : 'border-gray-200 dark:border-gray-700'
                                    }`}
                                >
                                    <div className="font-bold text-sm text-gray-900 dark:text-white">Değişken / Çoklu Vardiya Havuzu</div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Sabahçı/Akşamcı/Gececi gibi alternatif vardiyalar tanımlanır.</div>
                                </button>
                            </div>
                        )}

                        {/* Üstte Saat Seçimi (Tüm Vardiya Türlerinde Aktif) */}
                        <div className="p-4 rounded-2xl border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 space-y-4">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h4 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                        <Clock size={16} className="text-blue-600" />
                                        {wizardData.shiftType === 'Standard' ? 'Vardiya Başlangıç ve Bitiş Saatleri' : 'Referans / Varsayılan Başlangıç ve Bitiş Saatleri'}
                                    </h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {wizardData.shiftType === 'FlexibleWeekly'
                                            ? 'Tek bir iş gününün referans mesai baremini (izin/tatil/eksik gün dağılımı için) belirler.'
                                            : 'Günlük zorunlu net çalışma süresi, aşağıda seçtiğiniz saatlerden ücretsiz mola düşülerek otomatik hesaplanır.'}
                                    </p>
                                </div>
                                {!isFlexDurationModel && wizardData.workModel === 'variable' && (
                                    <button
                                        type="button"
                                        onClick={handleAddShift}
                                        className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold flex items-center gap-1 shadow-sm"
                                    >
                                        <Plus size={14} /> Alternatif Vardiya Ekle
                                    </button>
                                )}
                            </div>

                            <div className="space-y-3">
                                {wizardData.shifts.map((shift, index) => {
                                    const shiftCalc = calcShiftDurationInfo(shift);
                                    return (
                                        <div key={shift.id} className="p-3.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 space-y-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                                                    {wizardData.workModel === 'variable' ? `${index + 1}. Vardiya Dilimi` : 'Vardiya Saat Aralığı'}
                                                </span>
                                                <div className="flex items-center gap-2">
                                                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300">
                                                        Otomatik Günlük Net: {shiftCalc.net} dk ({(shiftCalc.net / 60).toFixed(1)} sa)
                                                    </span>
                                                    {wizardData.workModel === 'variable' && wizardData.shifts.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveShift(shift.id)}
                                                            className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg"
                                                            title="Vardiyayı Kaldır"
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Giriş Saati</label>
                                                    <input
                                                        type="time"
                                                        value={shift.startTime}
                                                        onChange={(e) => updateShift(shift.id, 'startTime', e.target.value)}
                                                        className="w-full px-3.5 py-2 text-sm font-semibold border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">Çıkış Saati</label>
                                                    <input
                                                        type="time"
                                                        value={shift.endTime}
                                                        onChange={(e) => updateShift(shift.id, 'endTime', e.target.value)}
                                                        className="w-full px-3.5 py-2 text-sm font-semibold border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    />
                                                </div>
                                            </div>

                                            {!isFlexDurationModel && (
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-gray-100 dark:border-gray-700/70">
                                                    <label className="flex items-start gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={shift.isNextDayShift}
                                                            onChange={(e) => updateShift(shift.id, 'isNextDayShift', e.target.checked)}
                                                            className="mt-0.5 h-4 w-4 text-indigo-600 rounded border-gray-300"
                                                        />
                                                        <span className="text-xs text-gray-700 dark:text-gray-300">
                                                            <strong>Gece Vardiyası:</strong> Çıkış saati ertesi takvim gününe sarkıyor.
                                                        </span>
                                                    </label>
                                                    <label className="flex items-start gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={shift.assignToShiftStartDate}
                                                            onChange={(e) => updateShift(shift.id, 'assignToShiftStartDate', e.target.checked)}
                                                            className="mt-0.5 h-4 w-4 text-indigo-600 rounded border-gray-300"
                                                        />
                                                        <span className="text-xs text-gray-700 dark:text-gray-300">
                                                            <strong>Başlangıç Gününe Yaz:</strong> Gece vardiyasını başladığı güne kaydet.
                                                        </span>
                                                    </label>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Pencereli Esnek Giriş Penceresi */}
                        {wizardData.shiftType === 'FlexibleWindow' && (
                            <div className="p-4 rounded-2xl bg-indigo-50/70 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 space-y-3">
                                <h4 className="text-sm font-bold text-indigo-800 dark:text-indigo-300 flex items-center gap-2">
                                    <Clock size={16} /> Pencereli Esnek Giriş Ayarları (Sliding Window)
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold uppercase text-indigo-900 dark:text-indigo-200 mb-1">En Erken Giriş Saati</label>
                                        <input
                                            type="time"
                                            value={wizardData.flexibleEntryStartTime}
                                            onChange={(e) => updateData('flexibleEntryStartTime', e.target.value)}
                                            className="w-full px-3.5 py-2 text-sm border border-indigo-300 dark:border-indigo-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase text-indigo-900 dark:text-indigo-200 mb-1">En Geç Giriş Saati (Tavan Sınır)</label>
                                        <input
                                            type="time"
                                            value={wizardData.flexibleEntryEndTime}
                                            onChange={(e) => updateData('flexibleEntryEndTime', e.target.value)}
                                            className="w-full px-3.5 py-2 text-sm border border-indigo-300 dark:border-indigo-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                </div>
                                <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={wizardData.limitEarlyEntryToWindow}
                                        onChange={(e) => updateData('limitEarlyEntryToWindow', e.target.checked)}
                                        className="mt-0.5 h-4 w-4 text-indigo-600 rounded border-gray-300"
                                    />
                                    <span className="text-xs text-indigo-900 dark:text-indigo-200">
                                        <strong>Erken Girişi Pencere Başlangıcına Kırp (Snap):</strong> Personel {wizardData.flexibleEntryStartTime} saatinden önce gelse bile mesaisi {wizardData.flexibleEntryStartTime}'da başlamış sayılır.
                                    </span>
                                </label>
                            </div>
                        )}

                        {/* Zorunlu Süre Kartı: Günlük Modellerde Otomatik, Haftalık Esnekte Haftalık Hedef */}
                        {wizardData.shiftType !== 'FlexibleWeekly' ? (
                            <div className="p-4 rounded-2xl bg-emerald-50/70 dark:bg-emerald-900/15 border border-emerald-200 dark:border-emerald-800/80">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-bold text-emerald-900 dark:text-emerald-300">
                                                Günlük Zorunlu Net Çalışma Süresi
                                            </span>
                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-emerald-600 text-white">
                                                Otomatik Hesaplanır
                                            </span>
                                        </div>
                                        <p className="mt-1 text-xs text-emerald-800 dark:text-emerald-300">
                                            Hesaplama motoru günlük bazda çalıştığı için kullanıcı hatasını önlemek amacıyla üstteki saat aralığından 4. adımdaki ücretsiz mola süresi düşülerek otomatik hesaplanır.
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2.5 bg-white dark:bg-gray-800 px-4 py-2.5 rounded-xl border border-emerald-300 dark:border-emerald-700 shadow-sm flex-shrink-0">
                                        <CheckCircle2 size={18} className="text-emerald-600 dark:text-emerald-400" />
                                        <div>
                                            <div className="text-base font-extrabold text-emerald-700 dark:text-emerald-300 leading-tight">
                                                {autoDailyNetMinutes} Dakika ({(autoDailyNetMinutes / 60).toFixed(1)} Saat)
                                            </div>
                                            <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                                Brüt {firstShiftCalc.gross} dk − {firstShiftCalc.unpaidBreak} dk ücretsiz mola
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="p-4 rounded-2xl bg-purple-50/70 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 space-y-4">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                    <h4 className="text-sm font-bold text-purple-900 dark:text-purple-200 flex items-center gap-2">
                                        <CalendarRange size={16} /> Haftalık Esnek Hedef ve Kurallar
                                    </h4>
                                    <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 bg-white dark:bg-gray-800 px-2.5 py-1 rounded-lg border border-emerald-200 dark:border-emerald-700">
                                        Günlük Referans Net: {autoDailyNetMinutes} dk ({(autoDailyNetMinutes / 60).toFixed(1)} saat)
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold uppercase text-purple-900 dark:text-purple-200 mb-1">
                                            Haftalık Zorunlu Çalışma Süresi
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="number"
                                                min="1"
                                                step={weeklyDurationUnit === 'hours' ? '0.5' : '1'}
                                                value={wizardData.requiredDurationMinutes !== '' && wizardData.requiredDurationMinutes !== null
                                                    ? (weeklyDurationUnit === 'hours' ? wizardData.requiredDurationMinutes / 60 : wizardData.requiredDurationMinutes)
                                                    : ''}
                                                onChange={(e) => {
                                                    const val = parseFloat(e.target.value);
                                                    if (isNaN(val)) {
                                                        updateData('requiredDurationMinutes', '');
                                                    } else {
                                                        updateData('requiredDurationMinutes', weeklyDurationUnit === 'hours' ? Math.round(val * 60) : Math.round(val));
                                                    }
                                                }}
                                                placeholder="Örn: 2700"
                                                className="w-full px-3.5 py-2 text-sm font-bold border border-purple-300 dark:border-purple-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            />
                                            <select
                                                value={weeklyDurationUnit}
                                                onChange={(e) => setWeeklyDurationUnit(e.target.value)}
                                                className="px-3 py-2 text-xs font-bold border border-purple-300 dark:border-purple-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            >
                                                <option value="minutes">Dakika</option>
                                                <option value="hours">Saat</option>
                                            </select>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 mt-2">
                                            <button
                                                type="button"
                                                onClick={() => updateData('requiredDurationMinutes', autoDailyNetMinutes * wizardData.workDays.length)}
                                                className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-emerald-100 text-emerald-800 hover:bg-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200"
                                            >
                                                {wizardData.workDays.length} Gün × Günlük Net ({autoDailyNetMinutes * wizardData.workDays.length} dk)
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => updateData('requiredDurationMinutes', 2700)}
                                                className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-purple-100 text-purple-800 hover:bg-purple-200 dark:bg-purple-900/40 dark:text-purple-200"
                                            >
                                                Standart 45 Saat (2700 dk)
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase text-purple-900 dark:text-purple-200 mb-1">
                                            Haftalık Hesap Başlangıç Günü
                                        </label>
                                        <select
                                            value={wizardData.weeklyFlexStartDay}
                                            onChange={(e) => updateData('weeklyFlexStartDay', e.target.value)}
                                            className="w-full px-3.5 py-2 text-sm border border-purple-300 dark:border-purple-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        >
                                            {DAYS_LIST.map(d => (
                                                <option key={d.key} value={d.key}>{d.full}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="space-y-2 pt-2 border-t border-purple-200/60 dark:border-purple-800/60">
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={wizardData.convertRemainingDaysToHoliday}
                                            onChange={(e) => updateData('convertRemainingDaysToHoliday', e.target.checked)}
                                            className="mt-0.5 h-4 w-4 text-purple-600 rounded border-gray-300"
                                        />
                                        <span className="text-xs text-purple-900 dark:text-purple-200">
                                            <strong>Hedef Dolduğunda Kalan Günleri Otomatik "Hafta Tatili (HT)" Say</strong>
                                        </span>
                                    </label>
                                    {wizardData.convertRemainingDaysToHoliday && (
                                        <label className="flex items-start gap-2.5 ml-6 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={wizardData.giveOvertimeOnRemainingDays}
                                                onChange={(e) => updateData('giveOvertimeOnRemainingDays', e.target.checked)}
                                                className="mt-0.5 h-4 w-4 text-purple-600 rounded border-gray-300"
                                            />
                                            <span className="text-xs text-purple-900 dark:text-purple-200">
                                                <strong>HT Sayılan Kalan Günlerde İşe Gelirse Hafta Tatili Fazla Mesaisi (HT FM) Yaz</strong>
                                            </span>
                                        </label>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                );

            case 3:
                return (
                    <div className="space-y-6">
                        {isFlexDurationModel && (
                            <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 text-xs text-purple-900 dark:text-purple-200 flex items-start gap-2.5">
                                <Info size={16} className="text-purple-600 flex-shrink-0 mt-0.5" />
                                <span>
                                    <strong>Süre Odaklı Esnek Model:</strong> Sabit bir giriş saati bulunmadığı için <em>"Geç Giriş"</em> cezası uygulanmaz. Aşağıdaki <strong>Eksik Süre Toleransı</strong>, hedef süreden kaç dakikaya kadar eksik çalışmanın tolere edileceğini belirler.
                                </span>
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {!isFlexDurationModel && (
                                <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                    <label className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-1">
                                        Geç Giriş Toleransı (dk)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={wizardData.entryToleranceMinutes}
                                        onChange={(e) => updateData('entryToleranceMinutes', parseInt(e.target.value, 10) || 0)}
                                        className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm font-bold"
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">Geç giriş sayılmayacak süre.</p>
                                </div>
                            )}
                            <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                <label className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-1">
                                    {isFlexDurationModel ? 'Eksik Süre Toleransı (dk)' : 'Erken Çıkış Toleransı (dk)'}
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={wizardData.exitToleranceMinutes}
                                    onChange={(e) => updateData('exitToleranceMinutes', parseInt(e.target.value, 10) || 0)}
                                    className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm font-bold"
                                />
                                <p className="text-[11px] text-gray-500 mt-1">
                                    {isFlexDurationModel ? 'Hedef süreden eksik çalışma toleransı.' : 'Erken çıkış sayılmayacak süre.'}
                                </p>
                            </div>
                            <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                <label className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-1">
                                    Min. Geçerli Mesai (dk)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={wizardData.minValidWorkMinutes}
                                    onChange={(e) => updateData('minValidWorkMinutes', parseInt(e.target.value, 10) || 0)}
                                    className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm font-bold"
                                />
                                <p className="text-[11px] text-gray-500 mt-1">Altında kalırsa "Yetersiz Çalışma" sayılır.</p>
                            </div>
                        </div>

                        {/* Tolerans İçi Kesinti / Kredi Politikası */}
                        <div className="p-4 rounded-xl bg-amber-50/60 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/70 space-y-3">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300">
                                Tolerans İçi Sapmaların Fiili Çalışma Süresine Etkisi
                            </h4>
                            {!isFlexDurationModel && (
                                <label className="flex items-start gap-2.5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={wizardData.deductLateEntryFromActual}
                                        onChange={(e) => updateData('deductLateEntryFromActual', e.target.checked)}
                                        className="mt-0.5 h-4 w-4 text-amber-600 rounded border-gray-300"
                                    />
                                    <div className="text-xs text-gray-800 dark:text-gray-200">
                                        <strong>Tolerans içi geç kalınırsa gecikme süresini fiili çalışmadan kes</strong>
                                        <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                            İşaretlenmezse, giriş toleransı ({wizardData.entryToleranceMinutes} dk) içindeki gecikmeler fiili çalışma süresine kredi olarak tamamlanır.
                                        </p>
                                    </div>
                                </label>
                            )}
                            <label className="flex items-start gap-2.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={wizardData.deductEarlyExitFromActual}
                                    onChange={(e) => updateData('deductEarlyExitFromActual', e.target.checked)}
                                    className="mt-0.5 h-4 w-4 text-amber-600 rounded border-gray-300"
                                />
                                <div className="text-xs text-gray-800 dark:text-gray-200">
                                    <strong>
                                        {isFlexDurationModel
                                            ? 'Tolerans içi eksik çalışmayı fiili süreden kes (Tamamlama yapma)'
                                            : 'Tolerans içi erken çıkılırsa eksik süreyi fiili çalışmadan kes'}
                                    </strong>
                                    <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                        İşaretlenmezse, tolerans ({wizardData.exitToleranceMinutes} dk) içindeki eksik süre tam mesaiye tamamlanır.
                                    </p>
                                </div>
                            </label>
                        </div>

                        {/* Turnike Yakalama Penceresi (Catchment) */}
                        <div className="p-4 rounded-xl bg-indigo-50/40 dark:bg-indigo-900/15 border border-indigo-200 dark:border-indigo-800/70">
                            <h4 className="text-xs font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 mb-3">
                                Turnike Okutma Yakalama Penceresi (Catchment Sapması)
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Giriş Yakalama Sapması (Saat)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        disabled={isFlexDurationModel}
                                        value={isFlexDurationModel ? 0 : wizardData.shiftCatchmentStartHours}
                                        onChange={(e) => updateData('shiftCatchmentStartHours', parseFloat(e.target.value) || 0)}
                                        className={`w-full px-3.5 py-2 border rounded-xl text-sm font-semibold ${
                                            isFlexDurationModel
                                                ? 'bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-800 cursor-not-allowed'
                                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600'
                                        }`}
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        {isFlexDurationModel
                                            ? 'Esnek vardiyada günün tamamı (00:00\'dan itibaren) yakalanır.'
                                            : 'Vardiya başlangıcından kaç saat önceki okutmalar yakalansın?'}
                                    </p>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                        Çıkış Yakalama Sapması (Saat)
                                    </label>
                                    <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        value={wizardData.shiftCatchmentEndHours}
                                        onChange={(e) => updateData('shiftCatchmentEndHours', parseFloat(e.target.value) || 0)}
                                        className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm font-semibold"
                                    />
                                    <p className="text-[11px] text-gray-500 mt-1">
                                        Vardiya bitişinden kaç saat sonraki çıkış okutmaları bu vardiyaya bağlansın?
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 4:
                return (
                    <div className="space-y-5">
                        <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400">
                                    <Coffee size={20} />
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-gray-900 dark:text-gray-100">Bu Çalışma Düzeninde Mola Tanımlansın mı?</h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Açık olduğunda mola kuralı otomatik oluşturulur ve vardiyaya bağlanır.
                                    </p>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={wizardData.hasBreak}
                                    onChange={(e) => updateData('hasBreak', e.target.checked)}
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                            </label>
                        </div>

                        {wizardData.hasBreak && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div className="sm:col-span-2">
                                        <label className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-1">Mola Adı</label>
                                        <input
                                            type="text"
                                            value={wizardData.breakName}
                                            onChange={(e) => updateData('breakName', e.target.value)}
                                            placeholder="Örn: Öğle Molası"
                                            className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm font-semibold"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-1">Mola Süresi (dk)</label>
                                        <input
                                            type="number"
                                            value={wizardData.breakDurationMinutes}
                                            readOnly={wizardData.breakType === 'FixedTime'}
                                            onChange={(e) => updateData('breakDurationMinutes', parseInt(e.target.value, 10) || 0)}
                                            className={`w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl text-sm font-bold ${
                                                wizardData.breakType === 'FixedTime'
                                                    ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                                                    : 'bg-white dark:bg-gray-700'
                                            }`}
                                        />
                                    </div>
                                </div>

                                {/* 3 Görsel Mola Tipi Kartı */}
                                <div>
                                    <label className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-2">Mola Zamanlama Modeli</label>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {[
                                            { id: 'FixedTime', title: 'Sabit Zamanlı', desc: 'Belirli saatler arası (Örn: 12:00-13:00)' },
                                            { id: 'FlexibleWindow', title: 'Pencereli Esnek', desc: 'Pencere içinde (Örn: 11:30-13:30) N dk' },
                                            { id: 'FlexibleAnytime', title: 'Tam Esnek', desc: 'Saat bağımsız gün içi toplam mola' }
                                        ].map(bt => (
                                            <button
                                                key={bt.id}
                                                type="button"
                                                onClick={() => updateData('breakType', bt.id)}
                                                className={`p-3 rounded-xl border text-left transition-all ${
                                                    wizardData.breakType === bt.id
                                                        ? 'border-indigo-600 bg-indigo-50/60 dark:bg-indigo-900/30 ring-2 ring-indigo-500/20'
                                                        : 'border-gray-200 dark:border-gray-700'
                                                }`}
                                            >
                                                <div className="font-bold text-xs text-gray-900 dark:text-white">{bt.title}</div>
                                                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{bt.desc}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {(wizardData.breakType === 'FixedTime' || wizardData.breakType === 'FlexibleWindow') && (
                                    <div className="grid grid-cols-2 gap-4 p-3.5 rounded-xl bg-blue-50/50 dark:bg-blue-900/15 border border-blue-200 dark:border-blue-800">
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Mola Pencere Başlangıç</label>
                                            <input
                                                type="time"
                                                value={wizardData.breakWindowStartTime}
                                                onChange={(e) => updateData('breakWindowStartTime', e.target.value)}
                                                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Mola Pencere Bitiş</label>
                                            <input
                                                type="time"
                                                value={wizardData.breakWindowEndTime}
                                                onChange={(e) => updateData('breakWindowEndTime', e.target.value)}
                                                className="w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Ücretli / Ücretsiz Seçimi */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => updateData('breakIsPaid', false)}
                                        className={`p-3 rounded-xl border text-left transition-all ${
                                            !wizardData.breakIsPaid
                                                ? 'border-amber-500 bg-amber-50/70 dark:bg-amber-900/20 ring-2 ring-amber-500/20'
                                                : 'border-gray-200 dark:border-gray-700'
                                        }`}
                                    >
                                        <div className="font-bold text-xs text-gray-900 dark:text-white">Ücretsiz Mola (Süreden Düşülür)</div>
                                        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                            Günlük net çalışma hedefini {firstShiftCalc.gross} dk'dan {autoDailyNetMinutes} dk'ya düşürür.
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => updateData('breakIsPaid', true)}
                                        className={`p-3 rounded-xl border text-left transition-all ${
                                            wizardData.breakIsPaid
                                                ? 'border-emerald-500 bg-emerald-50/70 dark:bg-emerald-900/20 ring-2 ring-emerald-500/20'
                                                : 'border-gray-200 dark:border-gray-700'
                                        }`}
                                    >
                                        <div className="font-bold text-xs text-gray-900 dark:text-white">Ücretli Mola (Çalışmış Sayılır)</div>
                                        <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                            Çalışma süresinden düşülmez; yalnızca mola aşımı varsa kesilir.
                                        </div>
                                    </button>
                                </div>

                                {!wizardData.breakIsPaid && (
                                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 space-y-3">
                                        <div>
                                            <label className="block text-xs font-bold uppercase text-gray-600 dark:text-gray-300 mb-1">Mola Düşüm Politikası</label>
                                            <select
                                                value={wizardData.breakDeductionPolicy}
                                                onChange={(e) => updateData('breakDeductionPolicy', e.target.value)}
                                                className="w-full px-3.5 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700"
                                            >
                                                <option value="DeductAlways">Turnikeye bakma, her zaman sabit {wizardData.breakDurationMinutes} dk kes (Deduct Always)</option>
                                                <option value="DeductActualUsage">Turnikeden dışarı çıkılan gerçek süre kadar kes (Deduct Actual Usage)</option>
                                            </select>
                                        </div>

                                        {wizardData.breakDeductionPolicy === 'DeductActualUsage' && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                                                <div>
                                                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Mola Az Kullanılırsa / Çıkılmazsa</label>
                                                    <select
                                                        value={wizardData.breakUnusedPolicy}
                                                        onChange={(e) => updateData('breakUnusedPolicy', e.target.value)}
                                                        className="w-full px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700"
                                                    >
                                                        <option value="ForceDeduct">Yine de tam mola süresini kes (Force Deduct)</option>
                                                        <option value="PayToEmployee">Kullanılmayan süreyi çalışana öde (Pay to Employee)</option>
                                                    </select>
                                                </div>
                                                {wizardData.breakUnusedPolicy === 'PayToEmployee' && (
                                                    <div className="flex items-center pt-4">
                                                        <label className="flex items-start gap-2 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={wizardData.breakConvertUnusedToOvertime}
                                                                onChange={(e) => updateData('breakConvertUnusedToOvertime', e.target.checked)}
                                                                className="mt-0.5 h-4 w-4 text-indigo-600 rounded border-gray-300"
                                                            />
                                                            <span className="text-xs text-gray-700 dark:text-gray-300">
                                                                Kullanılmayan molayı doğrudan <strong>Fazla Mesai (FM)</strong> yaz
                                                            </span>
                                                        </label>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <label className="flex items-start gap-2 pt-1 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={wizardData.breakIgnoreOnHolidays}
                                                onChange={(e) => updateData('breakIgnoreOnHolidays', e.target.checked)}
                                                className="mt-0.5 h-4 w-4 text-indigo-600 rounded border-gray-300"
                                            />
                                            <span className="text-xs text-gray-700 dark:text-gray-300">
                                                <strong>Hafta Tatili ve Resmi Tatil (HT/RT) çalışmalarında mola kesintisi yapma</strong>
                                            </span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );

            case 5:
                return (
                    <div className="space-y-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {!isFlexDurationModel && (
                                <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-3">
                                    <label className="flex items-start gap-2.5 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={wizardData.earlyOvertimeEnabled}
                                            disabled={wizardData.shiftType === 'FlexibleWindow' && wizardData.limitEarlyEntryToWindow}
                                            onChange={(e) => updateData('earlyOvertimeEnabled', e.target.checked)}
                                            className="mt-0.5 h-4 w-4 text-indigo-600 rounded border-gray-300"
                                        />
                                        <div className="text-xs">
                                            <span className="font-bold text-sm text-gray-900 dark:text-gray-100 block">Erken Fazla Mesai (Vardiya Öncesi FM)</span>
                                            <span className="text-gray-500 mt-0.5 block">
                                                Vardiya başlangıcından önce yapılan çalışmalar eşiği aşarsa FM sayılır.
                                            </span>
                                            {wizardData.shiftType === 'FlexibleWindow' && wizardData.limitEarlyEntryToWindow && (
                                                <span className="text-red-500 font-semibold mt-1 block">
                                                    * Pencereli Esnekte "Erken Girişi Kırp (Snap)" açık olduğu için erken FM kapalıdır.
                                                </span>
                                            )}
                                        </div>
                                    </label>

                                    {wizardData.earlyOvertimeEnabled && (
                                        <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                                            <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">Erken FM Eşiği (dk)</label>
                                            <input
                                                type="number"
                                                min="0"
                                                value={wizardData.earlyOvertimeThresholdMinutes}
                                                onChange={(e) => updateData('earlyOvertimeThresholdMinutes', parseInt(e.target.value, 10) || 0)}
                                                className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm font-bold"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="p-4 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-3">
                                <label className="flex items-start gap-2.5 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={wizardData.lateOvertimeEnabled}
                                        onChange={(e) => updateData('lateOvertimeEnabled', e.target.checked)}
                                        className="mt-0.5 h-4 w-4 text-indigo-600 rounded border-gray-300"
                                    />
                                    <div className="text-xs">
                                        <span className="font-bold text-sm text-gray-900 dark:text-gray-100 block">
                                            {isFlexDurationModel ? 'Hedef Süre Aşımı Fazla Mesai Aktif' : 'Geç Çıkış Fazla Mesai (Vardiya Sonrası FM)'}
                                        </span>
                                        <span className="text-gray-500 mt-0.5 block">
                                            {isFlexDurationModel
                                                ? 'Zorunlu net çalışma süresinin üzerindeki çalışmalar eşiği aşarsa FM sayılır.'
                                                : 'Vardiya bitiş saatinden sonra yapılan çalışmalar eşiği aşarsa FM sayılır.'}
                                        </span>
                                    </div>
                                </label>

                                {wizardData.lateOvertimeEnabled && (
                                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                                        <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1">Fazla Mesai Eşiği (dk)</label>
                                        <input
                                            type="number"
                                            min="0"
                                            value={wizardData.lateOvertimeThresholdMinutes}
                                            onChange={(e) => updateData('lateOvertimeThresholdMinutes', parseInt(e.target.value, 10) || 0)}
                                            className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm font-bold"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
                            <label className="block text-xs font-bold uppercase text-gray-700 dark:text-gray-300 mb-1">
                                Maksimum Günlük Fazla Mesai Tavanı (Dakika)
                            </label>
                            <input
                                type="number"
                                min="0"
                                value={wizardData.maxOvertimeMinutes}
                                onChange={(e) => updateData('maxOvertimeMinutes', parseInt(e.target.value, 10) || 0)}
                                className="w-48 px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-sm font-bold"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Bir günde onaylanacak maksimum fazla mesai sınırını belirler (Örn: 120 dk = 2 saat).
                            </p>
                        </div>
                    </div>
                );

            case 6:
                return (
                    <div className="space-y-5">
                        <div className="p-4 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-indigo-500/10 to-purple-500/10 border border-indigo-200 dark:border-indigo-800 flex items-center gap-3">
                            <div className="p-3 rounded-xl bg-emerald-600 text-white shadow-sm">
                                <CheckCircle2 size={22} />
                            </div>
                            <div>
                                <h3 className="text-base font-bold text-gray-900 dark:text-white">
                                    4 Katmanlı Vardiya & Puantaj Mimarisi Hazır!
                                </h3>
                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                    Onayladığınızda aşağıdaki <strong>Mola</strong>, <strong>Vardiya</strong>, <strong>Vardiya Grubu</strong> ve <strong>Çalışma Periyodu</strong> tek bir atomik işlemle oluşturulacaktır.
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-2">
                                <div className="text-[11px] font-bold uppercase text-amber-600 flex items-center gap-1.5">
                                    <Coffee size={14} /> 1. Katman: Mola Tanımı
                                </div>
                                <div className="font-bold text-sm text-gray-900 dark:text-white">
                                    {wizardData.hasBreak ? `${wizardData.breakName} (${wizardData.breakDurationMinutes} dk)` : 'Mola Tanımlanmadı'}
                                </div>
                                {wizardData.hasBreak && (
                                    <div className="text-gray-500">
                                        {wizardData.breakIsPaid ? 'Ücretli Mola' : `Ücretsiz Mola • ${wizardData.breakDeductionPolicy}`}
                                    </div>
                                )}
                            </div>

                            <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-2">
                                <div className="text-[11px] font-bold uppercase text-indigo-600 flex items-center gap-1.5">
                                    <Briefcase size={14} /> 2. Katman: Vardiya Kuralı ({wizardData.shifts.length} Adet)
                                </div>
                                <div className="font-bold text-sm text-gray-900 dark:text-white">
                                    {SHIFT_TYPE_LABELS[wizardData.shiftType]}
                                </div>
                                <div className="text-emerald-600 dark:text-emerald-400 font-semibold">
                                    {wizardData.shiftType === 'FlexibleWeekly'
                                        ? `Haftalık Hedef: ${wizardData.requiredDurationMinutes || 2700} dk (${((wizardData.requiredDurationMinutes || 2700) / 60).toFixed(1)} sa) • Günlük Ref: ${autoDailyNetMinutes} dk`
                                        : `Otomatik Günlük Net Hedef: ${autoDailyNetMinutes} dk (${(autoDailyNetMinutes / 60).toFixed(1)} saat)`}
                                </div>
                            </div>

                            <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-2">
                                <div className="text-[11px] font-bold uppercase text-purple-600 flex items-center gap-1.5">
                                    <Layers size={14} /> 3. Katman: Vardiya Grubu
                                </div>
                                <div className="font-bold text-sm text-gray-900 dark:text-white">
                                    {wizardData.groupName} {wizardData.shortCode && `(${wizardData.shortCode})`}
                                </div>
                                <div className="text-gray-500">
                                    {wizardData.shifts.map(s => `${s.startTime}-${s.endTime}`).join(', ')}
                                </div>
                            </div>

                            <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-2">
                                <div className="text-[11px] font-bold uppercase text-emerald-600 flex items-center gap-1.5">
                                    <Calendar size={14} /> 4. Katman: Çalışma Periyodu
                                </div>
                                <div className="font-bold text-sm text-gray-900 dark:text-white">
                                    {wizardData.groupName} Periyodu
                                </div>
                                <div className="text-gray-500">
                                    {wizardData.workDays.length} Çalışma Günü • {7 - wizardData.workDays.length} Hafta Tatili (HT)
                                </div>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    const effectiveSummaryTarget = wizardData.shiftType === 'FlexibleWeekly'
        ? `${wizardData.requiredDurationMinutes || 2700} dk (${((wizardData.requiredDurationMinutes || 2700) / 60).toFixed(1)} sa / hafta)`
        : `${autoDailyNetMinutes} dk (${(autoDailyNetMinutes / 60).toFixed(1)} sa / gün)`;

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col max-h-[92vh] overflow-hidden border border-gray-200 dark:border-gray-700">
                {/* Üst Gradient Başlık + Canlı Hesaplama Şeridi */}
                <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-purple-950 text-white px-6 py-4 border-b border-indigo-500/20">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-indigo-500/20 border border-indigo-400/30 text-indigo-300">
                                <Wand2 size={22} />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-white">
                                    Akıllı Mesai ve Vardiya Sihirbazı
                                </h2>
                                <p className="text-xs text-indigo-200/80">
                                    Mola, Vardiya, Vardiya Grubu ve Çalışma Periyodunu 6 adımda tek seferde uçtan uca kurgulayın.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded-lg text-indigo-200 hover:text-white hover:bg-white/10"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Canlı Özet Şeridi */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-3.5 pt-3 border-t border-white/10 text-xs">
                        <div className="bg-white/10 rounded-lg px-3 py-1.5">
                            <span className="text-indigo-200/70 block text-[10px] uppercase font-bold">Model</span>
                            <span className="font-bold text-white">{SHIFT_TYPE_CARDS.find(c => c.id === wizardData.shiftType)?.title}</span>
                        </div>
                        <div className="bg-white/10 rounded-lg px-3 py-1.5">
                            <span className="text-indigo-200/70 block text-[10px] uppercase font-bold">Otomatik Net Hedef</span>
                            <span className="font-bold text-emerald-300">{effectiveSummaryTarget}</span>
                        </div>
                        <div className="bg-white/10 rounded-lg px-3 py-1.5">
                            <span className="text-indigo-200/70 block text-[10px] uppercase font-bold">Mola Kuralı</span>
                            <span className="font-bold text-amber-300">
                                {wizardData.hasBreak ? `${wizardData.breakDurationMinutes} dk (${wizardData.breakIsPaid ? 'Ücretli' : 'Ücretsiz'})` : 'Mola Yok'}
                            </span>
                        </div>
                        <div className="bg-white/10 rounded-lg px-3 py-1.5">
                            <span className="text-indigo-200/70 block text-[10px] uppercase font-bold">Takvim</span>
                            <span className="font-bold text-purple-200">{wizardData.workDays.length} İş Günü / {7 - wizardData.workDays.length} HT</span>
                        </div>
                    </div>
                </div>

                {/* 6 Adımlı Görsel Stepper */}
                <div className="bg-gray-50 dark:bg-gray-900/60 px-6 py-2.5 border-b border-gray-200 dark:border-gray-700 grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {STEPS.map((step) => {
                        const Icon = step.icon;
                        const isActive = currentStep === step.id;
                        const isCompleted = currentStep > step.id;
                        return (
                            <div
                                key={step.id}
                                className={`px-2.5 py-1.5 rounded-xl flex items-center gap-2 text-xs transition-all ${
                                    isActive
                                        ? 'bg-indigo-600 text-white font-bold shadow-sm'
                                        : isCompleted
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 font-semibold'
                                        : 'text-gray-400 dark:text-gray-500'
                                }`}
                            >
                                <Icon size={14} className="flex-shrink-0" />
                                <span className="truncate">{step.id}. {step.title}</span>
                            </div>
                        );
                    })}
                </div>

                {/* İçerik Gövdesi */}
                <div className="p-6 flex-1 overflow-y-auto">
                    {validationError && (
                        <div className="mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-xl flex items-center text-sm">
                            <ShieldAlert size={18} className="mr-2 flex-shrink-0" />
                            {validationError}
                        </div>
                    )}
                    {renderStepContent()}
                </div>

                {/* Alt Navigasyon Barı */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/80">
                    <button
                        type="button"
                        onClick={handlePrev}
                        disabled={currentStep === 1}
                        className={`px-4 py-2.5 border rounded-xl text-sm font-semibold flex items-center ${
                            currentStep === 1
                                ? 'opacity-0 pointer-events-none'
                                : 'text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-100'
                        }`}
                    >
                        <ChevronLeft size={16} className="mr-1" /> Önceki Adım
                    </button>

                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900"
                        >
                            Vazgeç
                        </button>
                        {currentStep < 6 ? (
                            <button
                                type="button"
                                onClick={handleNext}
                                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-semibold flex items-center shadow-lg shadow-indigo-500/25"
                            >
                                Sonraki Adım <ChevronRight size={16} className="ml-1" />
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleFinish}
                                disabled={isSaving}
                                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold flex items-center shadow-lg shadow-emerald-500/25 disabled:opacity-70"
                            >
                                {isSaving ? (
                                    <>
                                        <Loader2 size={16} className="mr-2 animate-spin" /> Oluşturuluyor...
                                    </>
                                ) : (
                                    <>
                                        <Check size={16} className="mr-1.5" /> Tüm Yapıyı Oluştur ve Kaydet
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
